/**
 * Principal Schedule Management
 * - View/Create Observer-Child assignments with fixed schedules
 * - Today's call status dashboard
 * - Leave/Substitution management
 * - Call initiation with retry logic
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Calendar, Clock, Users, Phone, PhoneCall, PhoneOff,
  Plus, Trash2, RefreshCw, CheckCircle,
  XCircle, Loader2, ArrowRight, UserCheck,
  Repeat
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { principalNavItems } from '../config/principalNav';
import PrincipalQuickSchedule from '../components/PrincipalQuickSchedule';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function PrincipalScheduleManagement() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [principalName, setPrincipalName] = useState('Principal');
  const [activeTab, setActiveTab] = useState('today');
  const [loading, setLoading] = useState(true);
  
  // Today's schedule state
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [todayStats, setTodayStats] = useState({});
  const [todayDate, setTodayDate] = useState('');
  
  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [observers, setObservers] = useState([]);
  const [children, setChildren] = useState([]);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    observer_id: '',
    child_id: '',
    schedule: [],
    notes: ''
  });
  
  // Substitutions state
  const [substitutions, setSubstitutions] = useState([]);
  const [showSubstitutionForm, setShowSubstitutionForm] = useState(false);
  const [substitutionForm, setSubstitutionForm] = useState({
    assignment_id: '',
    substitute_observer_id: '',
    date: '',
    reason: 'leave'
  });

  const getToken = () => localStorage.getItem('principal_token');

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) { navigate('/principal/login'); return; }
    // fetch principal name
    axios.get(`${API_URL}/api/principal/dashboard?token=${token}`)
      .then(r => setPrincipalName(r.data?.principal?.name || 'Principal'))
      .catch(() => {});
    loadTodaySchedule();
    loadAssignments();
    loadObservers();
    loadChildren();
    loadSubstitutions();
  }, []);

  const loadTodaySchedule = async (date = null) => {
    try {
      setLoading(true);
      const token = getToken();
      const url = date 
        ? `${API_URL}/api/scheduling/today?token=${token}&date=${date}`
        : `${API_URL}/api/scheduling/today?token=${token}`;
      
      const response = await axios.get(url);
      setTodaySchedule(response.data.schedule || []);
      setTodayStats(response.data.stats || {});
      setTodayDate(response.data.date);
    } catch (err) {
      console.error('Error loading today schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/scheduling/assignments?token=${token}`);
      setAssignments(response.data.assignments || []);
    } catch (err) {
      console.error('Error loading assignments:', err);
    }
  };

  const loadObservers = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/principal/observers?token=${token}`);
      setObservers(response.data.observers || []);
    } catch (err) {
      console.error('Error loading observers:', err);
    }
  };

  const loadChildren = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/principal/children?token=${token}`);
      setChildren(response.data.children || response.data || []);
    } catch (err) {
      console.error('Error loading children:', err);
    }
  };

  const loadSubstitutions = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/scheduling/substitutions?token=${token}`);
      setSubstitutions(response.data.substitutions || []);
    } catch (err) {
      console.error('Error loading substitutions:', err);
    }
  };

  // Add time slot to schedule
  const addTimeSlot = () => {
    setAssignmentForm({
      ...assignmentForm,
      schedule: [...assignmentForm.schedule, { day: 'Monday', time: '10:00', duration_minutes: 30 }]
    });
  };

  // Remove time slot
  const removeTimeSlot = (index) => {
    const newSchedule = [...assignmentForm.schedule];
    newSchedule.splice(index, 1);
    setAssignmentForm({ ...assignmentForm, schedule: newSchedule });
  };

  // Update time slot
  const updateTimeSlot = (index, field, value) => {
    const newSchedule = [...assignmentForm.schedule];
    newSchedule[index][field] = value;
    setAssignmentForm({ ...assignmentForm, schedule: newSchedule });
  };

  // Create assignment
  const createAssignment = async () => {
    if (!assignmentForm.observer_id || !assignmentForm.child_id) {
      toast({ title: "Error", description: "Please select observer and child", variant: "destructive" });
      return;
    }
    if (assignmentForm.schedule.length === 0) {
      toast({ title: "Error", description: "Please add at least one time slot", variant: "destructive" });
      return;
    }

    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/scheduling/assignments?token=${token}`,
        assignmentForm
      );

      if (response.data.success) {
        toast({ title: "Success", description: "Assignment created successfully" });
        setShowAssignmentForm(false);
        setAssignmentForm({ observer_id: '', child_id: '', schedule: [], notes: '' });
        loadAssignments();
        loadTodaySchedule();
      }
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Failed to create assignment",
        variant: "destructive"
      });
    }
  };

  // Create substitution
  const createSubstitution = async () => {
    if (!substitutionForm.assignment_id || !substitutionForm.substitute_observer_id || !substitutionForm.date) {
      toast({ title: "Error", description: "Please fill all fields", variant: "destructive" });
      return;
    }

    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/scheduling/substitutions?token=${token}`,
        substitutionForm
      );

      if (response.data.success) {
        toast({ title: "Success", description: "Substitution created successfully" });
        setShowSubstitutionForm(false);
        setSubstitutionForm({ assignment_id: '', substitute_observer_id: '', date: '', reason: 'leave' });
        loadSubstitutions();
        loadTodaySchedule();
      }
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Failed to create substitution",
        variant: "destructive"
      });
    }
  };

  // Initiate call
  const initiateCall = async (assignmentId) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/scheduling/call-attempt/${assignmentId}?token=${token}`
      );

      if (response.data.success) {
        toast({ 
          title: "Call Initiated", 
          description: `Attempt #${response.data.attempt_number}` 
        });
        loadTodaySchedule();
      }
    } catch (err) {
      toast({ 
        title: "Call Failed", 
        description: err.response?.data?.detail || "Failed to initiate call",
        variant: "destructive"
      });
    }
  };

  // Deactivate assignment
  const deactivateAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to deactivate this assignment?')) return;

    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/scheduling/assignments/${assignmentId}?token=${token}`);
      toast({ title: "Success", description: "Assignment deactivated" });
      loadAssignments();
    } catch (err) {
      toast({ title: "Error", description: "Failed to deactivate", variant: "destructive" });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-gray-100 text-gray-600';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'retry_pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'no_answer': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'in_progress': return <PhoneCall className="w-4 h-4 animate-pulse" />;
      case 'retry_pending': return <Repeat className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'no_answer': return <PhoneOff className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <ResponsiveLayout navItems={principalNavItems} userRole="principal" userName={principalName} onLogout={handleLogout}>
      <div className="font-body space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
              <Calendar className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
              Schedule Management
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Manage Observer-Child assignments and daily schedules</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={activeTab === 'today' ? 'default' : 'outline'}
            onClick={() => setActiveTab('today')}
            className={`text-xs sm:text-sm ${activeTab === 'today' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Today's Schedule
          </Button>
          <Button
            variant={activeTab === 'quick-schedule' ? 'default' : 'outline'}
            onClick={() => setActiveTab('quick-schedule')}
            className={`text-xs sm:text-sm ${activeTab === 'quick-schedule' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <Phone className="w-4 h-4 mr-2" />
            Quick Schedule
          </Button>
          <Button
            variant={activeTab === 'assignments' ? 'default' : 'outline'}
            onClick={() => setActiveTab('assignments')}
            className={`text-xs sm:text-sm ${activeTab === 'assignments' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <Users className="w-4 h-4 mr-2" />
            Assignments
            <span className="ml-2 px-2 py-0.5 bg-sanjaya-orange text-sanjaya-navy text-xs rounded-full font-semibold">
              {assignments.length}
            </span>
          </Button>
          <Button
            variant={activeTab === 'substitutions' ? 'default' : 'outline'}
            onClick={() => setActiveTab('substitutions')}
            className={`text-xs sm:text-sm ${activeTab === 'substitutions' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Substitutions
          </Button>
        </div>

        {/* Quick Schedule Tab */}
        {activeTab === 'quick-schedule' && (
          <PrincipalQuickSchedule />
        )}

        {/* Today's Schedule Tab */}
        {activeTab === 'today' && (
          <div className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4">
              <div className="bg-sanjaya-navy text-white rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold font-heading">{todayStats.total_scheduled || 0}</p>
                <p className="text-xs sm:text-sm text-white/80">Total</p>
              </div>
              <div className="bg-sanjaya-teal text-white rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold font-heading">{todayStats.completed || 0}</p>
                <p className="text-xs sm:text-sm text-white/80">Completed</p>
              </div>
              <div className="bg-sanjaya-navy/70 text-white rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold font-heading">{todayStats.pending || 0}</p>
                <p className="text-xs sm:text-sm text-white/80">Pending</p>
              </div>
              <div className="bg-sanjaya-orange text-sanjaya-navy rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold font-heading">{todayStats.in_progress || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-navy/80">In Progress</p>
              </div>
              <div className="bg-slate-200 text-slate-700 rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold font-heading">{todayStats.cancelled || 0}</p>
                <p className="text-xs sm:text-sm text-slate-600">Cancelled</p>
              </div>
              <div className="bg-sanjaya-navy/10 text-sanjaya-navy rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-2xl font-bold font-heading">{todayStats.substitutions || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-navy/80">Substitutes</p>
              </div>
            </div>

            {/* Schedule Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-sanjaya-navy">Today's Calls — {todayDate}</CardTitle>
                  <CardDescription>Real-time call status for all scheduled sessions</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => loadTodaySchedule()} className="border-sanjaya-navy/30 text-sanjaya-navy">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : todaySchedule.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No sessions scheduled for today</p>
                    <p className="text-sm">Create assignments to see them here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Time</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Child</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Observer</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Attempts</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todaySchedule.map((item, idx) => (
                          <tr key={item.assignment_id || idx} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-3 font-mono font-semibold">{item.scheduled_time}</td>
                            <td className="py-3 px-3">{item.child_name}</td>
                            <td className="py-3 px-3">
                              <div>
                                <span>{item.observer_name}</span>
                                {item.is_substitute && (
                                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                    Sub ({item.substitution_reason})
                                  </span>
                                )}
                              </div>
                              {item.is_substitute && (
                                <div className="text-xs text-slate-400">
                                  Original: {item.original_observer_name}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(item.call_status)}`}>
                                {getStatusIcon(item.call_status)}
                                {item.call_status}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">{item.attempt_count}/3</td>
                            <td className="py-3 px-3">
                              {(item.call_status === 'pending' || item.call_status === 'retry_pending') && (
                                <Button
                                  size="sm"
                                  onClick={() => initiateCall(item.assignment_id)}
                                  className="bg-green-600 hover:bg-green-700 text-white h-8"
                                >
                                  <Phone className="w-3 h-3 mr-1" />
                                  {item.attempt_count > 0 ? 'Retry' : 'Call'}
                                </Button>
                              )}
                              {item.call_status === 'completed' && (
                                <span className="text-green-600 text-sm">
                                  {item.call_duration ? `${Math.floor(item.call_duration / 60)}:${(item.call_duration % 60).toString().padStart(2, '0')}` : 'Done'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="space-y-4">
            {/* Create Assignment Form */}
            {showAssignmentForm ? (
              <Card className="border-2 border-sanjaya-orange">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Create New Assignment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Observer *</Label>
                      <select
                        value={assignmentForm.observer_id}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, observer_id: e.target.value })}
                        className="mt-1 w-full h-10 px-3 border rounded-md"
                      >
                        <option value="">Select Observer</option>
                        {observers.map(o => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Child *</Label>
                      <select
                        value={assignmentForm.child_id}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, child_id: e.target.value })}
                        className="mt-1 w-full h-10 px-3 border rounded-md"
                      >
                        <option value="">Select Child</option>
                        {children.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Schedule Slots */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Weekly Schedule *</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addTimeSlot}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add Slot
                      </Button>
                    </div>
                    
                    {assignmentForm.schedule.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center border rounded-md bg-slate-50">
                        No time slots added. Click "Add Slot" to create schedule.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {assignmentForm.schedule.map((slot, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                            <select
                              value={slot.day}
                              onChange={(e) => updateTimeSlot(index, 'day', e.target.value)}
                              className="h-9 px-2 border rounded-md"
                            >
                              {DAYS.map(d => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                            <Input
                              type="time"
                              value={slot.time}
                              onChange={(e) => updateTimeSlot(index, 'time', e.target.value)}
                              className="w-32"
                            />
                            <select
                              value={slot.duration_minutes}
                              onChange={(e) => updateTimeSlot(index, 'duration_minutes', parseInt(e.target.value))}
                              className="h-9 px-2 border rounded-md"
                            >
                              <option value={15}>15 min</option>
                              <option value={30}>30 min</option>
                              <option value={45}>45 min</option>
                              <option value={60}>60 min</option>
                            </select>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTimeSlot(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Input
                      value={assignmentForm.notes}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                      placeholder="Optional notes..."
                      className="mt-1"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={createAssignment} className="bg-sanjaya-orange hover:bg-sanjaya-orange/90">
                      Create Assignment
                    </Button>
                    <Button variant="outline" onClick={() => setShowAssignmentForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button onClick={() => setShowAssignmentForm(true)} className="bg-sanjaya-navy">
                <Plus className="w-4 h-4 mr-2" />
                Create New Assignment
              </Button>
            )}

            {/* Assignments List */}
            <Card>
              <CardHeader>
                <CardTitle>Active Assignments</CardTitle>
                <CardDescription>Observer-Child pairs with fixed recurring schedules</CardDescription>
              </CardHeader>
              <CardContent>
                {assignments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No active assignments</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {assignments.map(assignment => (
                      <div key={assignment.id} className="border rounded-lg p-4 hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-sanjaya-navy">{assignment.child_name}</span>
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{assignment.observer_name}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {assignment.schedule?.map((slot, idx) => (
                                <span key={idx} className="px-2 py-1 bg-sanjaya-navy/10 text-sanjaya-navy text-xs rounded-full font-medium">
                                  {slot.day} {slot.time} ({slot.duration_minutes}min)
                                </span>
                              ))}
                            </div>
                            {assignment.notes && (
                              <p className="text-sm text-slate-500 mt-2">{assignment.notes}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deactivateAssignment(assignment.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Substitutions Tab */}
        {activeTab === 'substitutions' && (
          <div className="space-y-4">
            {/* Create Substitution Form */}
            {showSubstitutionForm ? (
              <Card className="border-2 border-sanjaya-orange">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading text-sanjaya-navy">
                    <UserCheck className="w-5 h-5" />
                    Create Substitution
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Assignment (Child) *</Label>
                      <select
                        value={substitutionForm.assignment_id}
                        onChange={(e) => setSubstitutionForm({ ...substitutionForm, assignment_id: e.target.value })}
                        className="mt-1 w-full h-10 px-3 border rounded-md"
                      >
                        <option value="">Select Assignment</option>
                        {assignments.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.child_name} (Observer: {a.observer_name})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Substitute Observer *</Label>
                      <select
                        value={substitutionForm.substitute_observer_id}
                        onChange={(e) => setSubstitutionForm({ ...substitutionForm, substitute_observer_id: e.target.value })}
                        className="mt-1 w-full h-10 px-3 border rounded-md"
                      >
                        <option value="">Select Observer</option>
                        {observers.map(o => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={substitutionForm.date}
                        onChange={(e) => setSubstitutionForm({ ...substitutionForm, date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Reason *</Label>
                      <select
                        value={substitutionForm.reason}
                        onChange={(e) => setSubstitutionForm({ ...substitutionForm, reason: e.target.value })}
                        className="mt-1 w-full h-10 px-3 border rounded-md"
                      >
                        <option value="leave">Planned Leave</option>
                        <option value="sick">Sick Leave</option>
                        <option value="emergency">Emergency</option>
                        <option value="training">Training</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={createSubstitution} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                      Create Substitution
                    </Button>
                    <Button variant="outline" onClick={() => setShowSubstitutionForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button onClick={() => setShowSubstitutionForm(true)} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                <Plus className="w-4 h-4 mr-2" />
                Create Substitution
              </Button>
            )}

            {/* Substitutions List */}
            <Card>
              <CardHeader>
                <CardTitle>Substitutions</CardTitle>
                <CardDescription>Temporary observer replacements</CardDescription>
              </CardHeader>
              <CardContent>
                {substitutions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <UserCheck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No substitutions</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Child</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Original</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Substitute</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Reason</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {substitutions.map(sub => (
                          <tr key={sub.id} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-3 font-mono">{sub.date}</td>
                            <td className="py-2 px-3">{sub.child_name}</td>
                            <td className="py-2 px-3">{sub.original_observer_name}</td>
                            <td className="py-2 px-3 font-medium text-sanjaya-teal">{sub.substitute_observer_name}</td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-0.5 bg-slate-100 rounded text-xs">{sub.reason}</span>
                            </td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded text-xs ${sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {sub.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ResponsiveLayout>
  );
}
